import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Fade,
    Slide,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import type Employee from '@src/Objects/Employee';
import { ArrowUp, Award, BarChart2, Circle, Crown, Minus, Star, Target, TrendingUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface SkillCheckerTabProps {
    employee: Employee;
}

function SkillCheckerTab({ employee }: SkillCheckerTabProps) {
    const theme = useTheme();
    const [viewMode, setViewMode] = useState<'bar' | 'radial'>('bar');
    const [selectedSkills, setSelectedSkills] = useState<Array<{ id: number; title: string; level: number }>>([]);
    const [animatedValues, setAnimatedValues] = useState<{ [key: string]: number }>({});

    const levelColors = [
        theme.palette.primary.light,
        theme.palette.primary.main,
        theme.palette.primary.dark,
        theme.palette.secondary.main,
    ];
    const teamColor = theme.palette.primary.main + '80';
    const companyColor = theme.palette.secondary.main + '80';

    useEffect(() => {
        const newValues: { [key: string]: number } = {};
        selectedSkills.forEach((skill) => {
            newValues[skill.title] = 0;
            newValues[skill.title + '_team'] = 0;
            newValues[skill.title + '_company'] = 0;
        });
        setAnimatedValues(newValues);

        const interval = setInterval(() => {
            setAnimatedValues((prev) => {
                const updated: { [key: string]: number } = { ...prev };
                let allDone = true;

                selectedSkills.forEach((skill) => {
                    const targets = {
                        user: skill.level,
                        team: Math.floor(Math.random() * 4 + 1),
                        company: Math.floor(Math.random() * 4 + 1),
                    };

                    ['user', 'team', 'company'].forEach((key) => {
                        const mapKey =
                            key === 'user'
                                ? skill.title
                                : key === 'team'
                                  ? skill.title + '_team'
                                  : skill.title + '_company';
                        if (updated[mapKey] < targets[key as keyof typeof targets]) {
                            updated[mapKey] = Math.min(updated[mapKey] + 0.1, targets[key as keyof typeof targets]);
                            allDone = false;
                        }
                    });
                });

                if (allDone) clearInterval(interval);
                return updated;
            });
        }, 20);

        return () => clearInterval(interval);
    }, [selectedSkills]);

    const getCategoryIcon = (level: number) => {
        if (level >= 4) return <Award size={16} />;
        if (level === 3) return <Target size={16} />;
        return <TrendingUp size={16} />;
    };

    const getLevelText = (level: number) => {
        switch (level) {
            case 1:
                return 'Junior';
            case 2:
                return 'Intermediate';
            case 3:
                return 'Senior';
            case 4:
                return 'Expert';
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                Skill Level Checker
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                Wähle einen Skill und vergleiche dich mit Peers
            </Typography>

            {/* Skill Selector */}
            <Autocomplete
                multiple
                options={employee.skills || []}
                getOptionLabel={(option) => option.title}
                value={selectedSkills}
                onChange={(_, newValue) => setSelectedSkills(newValue)}
                renderInput={(params) => <TextField {...params} label="Skills auswählen" />}
                sx={{ mb: 3 }}
            />

            {/* View Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: 1 }}>
                <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
                    <ToggleButton value="bar">
                        <BarChart2 size={16} />
                    </ToggleButton>
                    <ToggleButton value="radial">
                        <Circle size={16} />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {selectedSkills.length > 0 ? (
                <Box sx={{ position: 'relative', minHeight: 220 }}>
                    {/* Bar View */}
                    <Fade in={viewMode === 'bar'} timeout={400} unmountOnExit>
                        <Slide direction="up" in={viewMode === 'bar'} timeout={400} mountOnEnter unmountOnExit>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {selectedSkills.map((skill, index) => {
                                    const userLevel = animatedValues[skill.title] || 0;
                                    const teamLevel =
                                        animatedValues[skill.title + '_team'] || Math.floor(Math.random() * 4 + 1);
                                    const companyLevel =
                                        animatedValues[skill.title + '_company'] || Math.floor(Math.random() * 4 + 1);

                                    const bars = [
                                        {
                                            label: 'Du',
                                            level: userLevel,
                                            color:
                                                levelColors[Math.min(Math.floor(userLevel) - 1, 3)] || levelColors[0],
                                            tooltip: `Dein Level: ${skill.level}/4`,
                                        },
                                        {
                                            label: 'Team Ø',
                                            level: teamLevel,
                                            color:
                                                levelColors[Math.min(Math.floor(teamLevel) - 1, 3)] || levelColors[0],
                                            tooltip: `Team-Durchschnitt: ${teamLevel.toFixed(1)}/4`,
                                        },
                                        {
                                            label: 'Company Ø',
                                            level: companyLevel,
                                            color:
                                                levelColors[Math.min(Math.floor(companyLevel) - 1, 3)] ||
                                                levelColors[0],
                                            tooltip: `Unternehmens-Durchschnitt: ${companyLevel.toFixed(1)}/4`,
                                        },
                                    ];

                                    return (
                                        <Box key={index}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                    {skill.title}
                                                </Typography>
                                                <Chip
                                                    icon={getCategoryIcon(skill.level)}
                                                    label={getLevelText(skill.level)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${levelColors[skill.level - 1]}20`,
                                                        color: levelColors[skill.level - 1],
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            </Box>

                                            {/* Comparison Chip */}
                                            <Box sx={{ mb: 1 }}>
                                                {userLevel > teamLevel && userLevel > companyLevel ? (
                                                    <Chip
                                                        icon={<Crown size={14} />}
                                                        label="Du führst in diesem Skill"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.success.main + '20',
                                                            color: theme.palette.success.main,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                ) : userLevel === Math.max(userLevel, teamLevel, companyLevel) ? (
                                                    <Chip
                                                        icon={<Star size={14} />}
                                                        label="Du bist Spitze in diesem Skill"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.warning.main + '20',
                                                            color: theme.palette.warning.main,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                ) : userLevel > (teamLevel + companyLevel) / 2 ? (
                                                    <Chip
                                                        icon={<ArrowUp size={14} />}
                                                        label="Du liegst über dem Durchschnitt"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.info.main + '20',
                                                            color: theme.palette.info.main,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<TrendingUp size={14} />}
                                                        label="Hier kannst du noch wachsen"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.primary.main + '20',
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Side-by-side Level Bars */}
                                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                {bars.map((bar, i) => (
                                                    <Tooltip key={i} title={bar.tooltip} placement="top">
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: bar.color,
                                                                    fontWeight: 600,
                                                                    mb: 0.5,
                                                                    display: 'block',
                                                                }}
                                                            >
                                                                {bar.label}: {bar.level.toFixed(1)}/4
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 16,
                                                                    borderRadius: 8,
                                                                    bgcolor: 'rgba(0,0,0,0.05)',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: `${(bar.level / 4) * 100}%`,
                                                                        height: '100%',
                                                                        bgcolor: bar.color,
                                                                        borderRadius: 8,
                                                                        transition: 'width 0.4s ease',
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Slide>
                    </Fade>

                    {/* Radial View */}
                    <Fade in={viewMode === 'radial'} timeout={400} unmountOnExit>
                        <Slide direction="up" in={viewMode === 'radial'} timeout={400} mountOnEnter unmountOnExit>
                            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'space-around', flexWrap: 'wrap' }}>
                                {selectedSkills.map((skill, index) => {
                                    const userLevel = animatedValues[skill.title] || 0;
                                    const teamLevel =
                                        animatedValues[skill.title + '_team'] || Math.floor(Math.random() * 4 + 1);
                                    const companyLevel =
                                        animatedValues[skill.title + '_company'] || Math.floor(Math.random() * 4 + 1);

                                    return (
                                        <Box
                                            key={index}
                                            sx={{ textAlign: 'center', position: 'relative', minWidth: 120 }}
                                        >
                                            {/* Concentric Circles */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip
                                                    title={`Unternehmens-Durchschnitt: ${companyLevel.toFixed(1)}/4`}
                                                >
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={(companyLevel / 4) * 100}
                                                        size={100}
                                                        thickness={3}
                                                        sx={{
                                                            color: companyColor,
                                                            position: 'absolute',
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                </Tooltip>

                                                <Tooltip title={`Team-Durchschnitt: ${teamLevel.toFixed(1)}/4`}>
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={(teamLevel / 4) * 100}
                                                        size={80}
                                                        thickness={4}
                                                        sx={{
                                                            color: teamColor,
                                                            position: 'absolute',
                                                            opacity: 0.8,
                                                        }}
                                                    />
                                                </Tooltip>

                                                <Tooltip title={`Dein Level: ${skill.level}/4`}>
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={(userLevel / 4) * 100}
                                                        size={60}
                                                        thickness={6}
                                                        sx={{ color: levelColors[skill.level - 1] }}
                                                    />
                                                </Tooltip>

                                                {/* Center Content */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 600, color: levelColors[skill.level - 1] }}
                                                    >
                                                        {userLevel.toFixed(1)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 500 }}>
                                                {skill.title}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Slide>
                    </Fade>
                </Box>
            ) : (
                <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                    Wähle einen Skill aus, um den Vergleich zu sehen
                </Typography>
            )}

            {/* Development Actions */}
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 2, textAlign: 'center' }}>
                    Verbessere deine Skills durch gezieltes Lernen und praktische Anwendung
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        Neue Ziele setzen
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': { backgroundColor: theme.palette.primary.main + '08' },
                        }}
                    >
                        Lernpfade entdecken
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': { backgroundColor: theme.palette.primary.main + '08' },
                        }}
                    >
                        Mentoring anfragen
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default SkillCheckerTab;
